import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useIntl, history, getLocale, connect } from 'umi';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { commonValueFormatter, formatPercentNum, splitDate } from '@/pages/dashboard/utils/utils';
import { addChartLabel, xAxisHighLight, xAxisLabelFormat } from '@/pages/dashboard/components/Chart/_config/plug';
import CardBox from '@/pages/dashboard/components/CardBox';
import useData from './useData';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { dynamicString } from '@home/utils/dynamicString';
import dataCommonFormat from '@home/utils/dbDataFormat/dataCommonFormat';
import { resolveAndSelectName, prepareMetricsDataSource } from '@/pages/dashboard/utils/metricUtils';
import { isArray } from 'lodash-es';
import { LineChartProps } from '@/pages/home/components/Chart/_config/interface';

import './index.scss';

interface dMetricsArrProps {
  [key: string]: any;
}

const RetentionTrendCo = (props: any) => {
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEn = getLocale() === 'en-US';
  const { gameCode, data, style, className, config, segmentVal } = props;
  const { tag } = data || {};
  const metricsInfo = props?.data?.metricsInfo;
  const { title, titleCN, api, componentMetrics, isHaveTitle, isHaveSubTitle, enChartTooltipsName, cnChartTooltipsName, iconList = ['vs', 'eyes'] } = config || {};
  const tooltipTitleText = isEn ? enChartTooltipsName : cnChartTooltipsName;
  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const metricKeys = useMemo(() => {
    return componentMetrics?.map((item: { metricKey: string; }) => item.metricKey) as string[] | undefined;
  }, [componentMetrics]);
  const extraParams = useMemo(() => {
    if (segmentVal === 'aggs') {
      return { logical_flag: 'weighted_daily_average' };
    }
    return undefined;
  }, [segmentVal]);
  const retentionTrendData = useData(api, filterVal, filterLoading, tag, extraParams, metricKeys);
  const {
    componentData,
    error,
    loading,
  } = retentionTrendData;
  // const { d1, d2, d3, d7 } = componentData;

  // 准备 metricsData 用于 dynamicString 解析
  const metricsData = useMemo(() => {
    return prepareMetricsDataSource(metricsInfo);
  }, [metricsInfo]);

  const [iseye, setIseye] = useState(true);
  const [isinit, setIsInit] = useState(true);

  // 按 componentMetrics 配置顺序取对应的趋势数据，保证数据与图例一一对应
  const dMetricsArr: dMetricsArrProps[] = useMemo(() => {
    if (componentMetrics?.length) {
      return componentMetrics
        .map((item: { metricKey: string; }) => componentData[item.metricKey])
        .filter((v: unknown) => isArray(v));
    }
    const result: dMetricsArrProps[] = [];
    Object.keys(componentData).forEach((key) => {
      if (isArray(componentData[key])) {
        result.push(componentData[key]);
      }
    });
    return result;
  }, [componentData, componentMetrics]);

  useEffect(() => {
    setIsInit(false);
  }, []);

  const legendData = useMemo(() => {
    return componentMetrics?.map((item: { enName: string; cnName: string; metricKey?: string; }) => {
      // 支持动态字符串解析指标名称
      const name = resolveAndSelectName(isEn, item?.enName, item?.cnName, metricsInfo) || (isEn ? item?.enName : item?.cnName) || '';
      return name;
    });
  }, [isEn, componentMetrics, metricsInfo]);

  const xAxisData = useMemo(() => {
    const maxLengthArray = dMetricsArr.reduce((acc, curr) => (curr?.length > acc?.length ? curr : acc), []);
    return maxLengthArray.map((item: { time_dot: string; }) => splitDate(item?.time_dot));
  }, [componentData, dMetricsArr]);

  const lineSeries = useMemo(() => {
    const newList = dMetricsArr;
    return newList.map((item: any, index: number) => ({
      name: legendData[index],
      type: 'line',
      data: item?.map((item2: any) => item2?.count) || [],
      symbol: 'circle',
    }));
  }, [componentData, legendData, dMetricsArr]) || [];

  // 无数据
  const isNoData = lineSeries.reduce((pre, current) => {
    return pre && (current?.data?.filter((item: any) => item).length === 0);
  }, true);

  // 折线图浮窗展示：
  const lineFormatterFunc = useMemo(() => {
    return (param: string | any[]) => {
      let returnStr = `<div class=${'basic-formatter-box'}>
      <div class=${'basic-formatter-header'}>
      <p class='basic-formatter-one basic-formatter-title'>${param[0].axisValue}</p>
      <p class='basic-formatter-two basic-formatter-title'>${tooltipTitleText}</p></div>`;

      for (let i = 0;i < param.length;i++) {
        const dauValue = param[i].value;
        const flag = param[i].value === '' || param[i].value === null || param[i].value === undefined;
        const name = param[i].seriesName;
        // 获取当前指标对应的配置
        const metricConfig = componentMetrics?.[i];
        const formatRule = metricConfig?.formatRule;
        
        // 格式化数值：优先使用 formatRule，否则使用 formatPercentNum（兼容旧逻辑）
        let formattedValue = '-';
        if (!flag) {
          if (formatRule) {
            const resolvedFormatRule = dynamicString(formatRule as string, metricsData);
            const formatted = dataCommonFormat({ value: dauValue, formatRule: resolvedFormatRule });
            formattedValue = String((formatted ?? formatPercentNum(parseFloat(dauValue))) || '-');
          } else {
            formattedValue = String(formatPercentNum(parseFloat(dauValue)) || '-');
          }
        }
        
        returnStr += `<div class=${'basic-formatter-header'}>
      <p class=${'basic-formatter-one'}>${param[i].marker}&nbsp;${name}</p>
      <p class=${'basic-formatter-two'}>${formattedValue}</p></div>`;
      }
      returnStr = `${returnStr}</div>`;
      return returnStr;
    };
  }, [componentMetrics, metricsData, tooltipTitleText]);

  const subTitleCom = useMemo(() => {
    // 粒度：Day / Week / Month / WeekAggs / MonthAggs
    const timeType = filterVal?.timeType || 'Day';

    return <div className='register-table-time' style={{ marginBottom: 10 }}>
      {
        (loading || filterLoading || error || isNoData) ?
          <div style={{ height: 21 }}></div> :
          <>{
            componentMetrics?.map(((item: any, index: number) => {
              // 只有 segment 选中 aggs 且有 rangeNameByGranularity 配置时才使用
              const useGranularityConfig = segmentVal === 'aggs' && item?.rangeNameByGranularity;
              const granularityConfig = useGranularityConfig ? (item?.rangeNameByGranularity?.[timeType?.toLowerCase()] || {}) : {};
              const rawRangeEnName = granularityConfig.rangeEnName ?? item?.rangeEnName;
              const rawRangeCnName = granularityConfig.rangeCnName ?? item?.rangeCnName;

              const rangeName =
                resolveAndSelectName(isEn, rawRangeEnName, rawRangeCnName, metricsInfo) ||
                (isEn ? rawRangeEnName : rawRangeCnName) ||
                '';

              const value = componentData?.[item?.avgResKey] as string | undefined;
              const formatRule = item?.formatRule;
              
              // 格式化数值：优先使用 formatRule，否则使用 commonValueFormatter（兼容旧逻辑）
              let formattedValue = '-';
              if (value !== '' && value !== null && value !== undefined) {
                if (formatRule) {
                  const resolvedFormatRule = dynamicString(formatRule as string, metricsData);
                  const formatted = dataCommonFormat({ value, formatRule: resolvedFormatRule });
                  formattedValue = String((formatted ?? commonValueFormatter(item?.type || 'int', value, item?.decimal || 0)) || '-');
                } else {
                  formattedValue = String(commonValueFormatter(item?.type || 'int', value, item?.decimal || 0) || '-');
                }
              }
              
              return <span key={index + item?.avgResKey} className='time-text'>{rangeName} : {formattedValue}</span>;
            }))
          }
          </>
      }
    </div>;
  }, [loading, filterLoading, error, isNoData, componentMetrics, componentData, isEn, metricsInfo, metricsData, segmentVal, filterVal?.timeType]);

  const renderList = [
    {
      type: 'vs',
      svgType: 'vs-icon',
      content: formatMessage({ id: 'common_comparison_analysis' }),
      isHideIcon: !iconList?.includes('vs'),
      isHideCom: true,
    },
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      index: 0,
      isHideIcon: !iconList?.includes('eyes'),
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      // isHideIcon: true,
      isHideIcon: !iconList?.includes('lineChart'),
      component: <LineChart
        height={300}
        legendData={legendData || []}
        xAxisData={xAxisData || []}
        series={lineSeries || []}
        formatterFunc={lineFormatterFunc}
        setOtherConfigFunc={(opt: LineChartProps) => {
          opt = xAxisLabelFormat(opt);
          
          // 获取第一个指标的 formatRule（用于 yAxis 格式化，如果所有指标使用相同格式）
          const firstMetricFormatRule = componentMetrics?.[0]?.formatRule;
          
          // yAxis formatter：优先使用 formatRule，否则使用 formatPercentNum（兼容旧逻辑）
          if ((opt as any).yAxis?.axisLabel) {
            (opt as any).yAxis.axisLabel.formatter = (value: any) => {
              if (firstMetricFormatRule) {
                const resolvedFormatRule = dynamicString(firstMetricFormatRule as string, metricsData);
                const formatted = dataCommonFormat({ value: parseFloat(value), formatRule: resolvedFormatRule });
                return String((formatted ?? formatPercentNum(parseFloat(value), 2)) || '');
              }
              return String(formatPercentNum(parseFloat(value), 2) || '');
            };
          }
          
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; seriesIndex?: number; }) => {
              // 根据 seriesIndex 获取对应指标的 formatRule
              const metricConfig = componentMetrics?.[params?.seriesIndex || 0];
              const formatRule = metricConfig?.formatRule;
              
              if (formatRule) {
                const resolvedFormatRule = dynamicString(formatRule as string, metricsData);
                const formatted = dataCommonFormat({ value: params?.value, formatRule: resolvedFormatRule });
                return String((formatted ?? formatPercentNum(parseFloat(params?.value as string), 2)) || '');
              }
              return String(formatPercentNum(parseFloat(params?.value as string), 2) || '');
            },
            show: iseye,
            formatNum: (value: string | number, seriesIndex?: number) => {
              // 根据 seriesIndex 获取对应指标的 formatRule
              const metricConfig = componentMetrics?.[seriesIndex || 0];
              const formatRule = metricConfig?.formatRule;
              
              if (formatRule) {
                const resolvedFormatRule = dynamicString(formatRule as string, metricsData);
                const formatted = dataCommonFormat({ value, formatRule: resolvedFormatRule });
                return String((formatted ?? formatPercentNum(parseFloat(value as string), 2)) || '');
              }
              return String(formatPercentNum(parseFloat(value as string), 2) || '');
            }
          }, componentMetrics
            ?.map((item: { showLabel?: boolean; }, i: number) => item.showLabel !== false ? i : -1)
            .filter((i: number) => i !== -1) ?? [0, 1, 2]);
          opt = xAxisHighLight(opt, {
            formatter: (params: any) => {
              return params.value ? params.value?.slice(-5) : '';
            },
            lineStyle: {
              type: 'solid'
            }
          });
          return opt;
        }}
      />
    },
  ];
  return <div style={style} className={`dbd-retention-neightingale-line-chart ${className}`}>
    <CardBox
      className='register-table'
      defaultAvtive={2}
      height={300}
      bordered={false}
      titleCom={isHaveTitle ? (isEn ? title : titleCN) : ''}
      subTitleCom={isHaveSubTitle ? subTitleCom : ''}
      renderList={renderList}
      setCurrentDataType={(type) => {
        if (!isinit) {
          if (type === 'eyes') {
            setIseye(!iseye);
          }
          if (type === 'vs') {
            const newFormValue: any = {
              timeType: filterVal?.timeType || 'Day',
              filterValue: {
                ...filterVal
              }
            };
            history.push(
              `/dashboard/game/${gameCode}/comparisonAnalysis/dateComparison`,
              JSON.stringify({ filterParams: newFormValue, indicators: componentMetrics?.metricKey, hasBack: true }),
            );
            return;
          }
        }
      }}
      isLoading={loading}
      isError={error}
      isNodata={isNoData}
    />
  </div>;

};

export default connect((state: any) => {
  return {
    gameCode: state.dashboard?.currentGame?.game_code,
  };
})(RetentionTrendCo);
